/**
 * Power spectrum for relative alpha: Welch's method with Hann-windowed, linearly detrended segments.
 * At the Muse's 256 Hz a 256-sample segment gives 1 Hz bins, so bin k is k Hz.
 */

/** In-place iterative radix-2 FFT. `re` and `im` have the same power-of-two length. */
export function fft(re: Float64Array, im: Float64Array): void {
  const n = re.length;
  if (n & (n - 1)) throw new RangeError("fft length must be a power of two");
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      [re[i], re[j]] = [re[j]!, re[i]!];
      [im[i], im[j]] = [im[j]!, im[i]!];
    }
  }
  for (let size = 2; size <= n; size <<= 1) {
    const half = size >> 1, step = (-2 * Math.PI) / size;
    for (let start = 0; start < n; start += size) {
      for (let k = 0; k < half; k++) {
        const a = start + k, b = a + half;
        const wr = Math.cos(step * k), wi = Math.sin(step * k);
        const tr = re[b]! * wr - im[b]! * wi, ti = re[b]! * wi + im[b]! * wr;
        re[b] = re[a]! - tr; im[b] = im[a]! - ti;
        re[a] = re[a]! + tr; im[a] = im[a]! + ti;
      }
    }
  }
}

/** Removes the least-squares straight line, so slow electrode drift does not leak into low bins. */
export function detrend(x: ArrayLike<number>): Float64Array {
  const n = x.length, out = new Float64Array(n);
  const mid = (n - 1) / 2;
  let mean = 0;
  for (let i = 0; i < n; i++) mean += x[i]!;
  mean /= n;
  let num = 0, den = 0;
  for (let i = 0; i < n; i++) { num += (i - mid) * (x[i]! - mean); den += (i - mid) * (i - mid); }
  const slope = den ? num / den : 0;
  for (let i = 0; i < n; i++) out[i] = x[i]! - mean - slope * (i - mid);
  return out;
}

/**
 * Welch power spectral density (relative units: ratios are what matter here). `segment` is a power of two;
 * segments overlap by half. Returns one value per bin 0 … segment/2, bin width rate/segment Hz.
 */
export function welch(x: ArrayLike<number>, segment: number): Float64Array {
  const bins = segment / 2 + 1;
  const psd = new Float64Array(bins);
  const hann = new Float64Array(segment);
  for (let i = 0; i < segment; i++) hann[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (segment - 1));
  const hop = segment / 2;
  let count = 0;
  for (let start = 0; start + segment <= x.length; start += hop) {
    const seg = detrend(Array.prototype.slice.call(x, start, start + segment) as number[]);
    const re = new Float64Array(segment), im = new Float64Array(segment);
    for (let i = 0; i < segment; i++) re[i] = seg[i]! * hann[i]!;
    fft(re, im);
    for (let k = 0; k < bins; k++) psd[k] = psd[k]! + re[k]! * re[k]! + im[k]! * im[k]!;
    count++;
  }
  if (count) for (let k = 0; k < bins; k++) psd[k] = psd[k]! / count;
  return psd;
}

/** Sum of PSD bins whose centre frequency lies in [lo, hi] Hz, inclusive. */
export function bandPower(psd: Float64Array, binHz: number, [lo, hi]: readonly [number, number]): number {
  let sum = 0;
  for (let k = Math.ceil(lo / binHz); k <= Math.floor(hi / binHz) && k < psd.length; k++) sum += psd[k]!;
  return sum;
}
